import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  LinearProgress,
  Button,
  Rating,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { StarRounded as StarIcon, Verified } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";

import FullDetails from "./FullDetails";
import ShortDetails from "./ShortDetails";
import SEOHelmet from "../../../Components/SeoHelmet";

import { useGetGymByIdQuery } from "../../../Store/api/queries";
import {
  useAddRatingMutation,
  useGetGymRatingQuery,
  useGetMyGymRatingQuery,
} from "../../../Store/api/ratingApi";
import { showGlobalToast } from "../../../Components/ToastProvider";

export default function GymDetails() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading } = useGetGymByIdQuery(id);

  const token = useSelector((state) => state.userSlice?.token);

  const {
    data: gymRatingData,
    isLoading: isGymRatingLoading,
    isFetching: isGymRatingFetching,
  } = useGetGymRatingQuery(id, {
    skip: !id,
  });

  const {
    data: myRatingData,
    isLoading: isMyRatingLoading,
    isFetching: isMyRatingFetching,
  } = useGetMyGymRatingQuery(id, {
    skip: !id || !token,
  });

  const [addRating, { isLoading: isRatingSubmitting }] = useAddRatingMutation();

  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [userRatingValue, setUserRatingValue] = useState(0);

  const gymData = data?.data || {};

  const averageRating = Number(
    gymRatingData?.data?.averageRating ||
      gymRatingData?.data?.average_rating ||
      gymData?.rating_stats?.average_rating ||
      0,
  );

  const totalRatings = Number(
    gymRatingData?.data?.totalRatings ||
      gymRatingData?.data?.total_ratings ||
      gymData?.rating_stats?.total_reviews ||
      0,
  );

  const hasRated = Boolean(myRatingData?.data?.has_rated);
  const myRating = Number(myRatingData?.data?.rating || 0);

  useEffect(() => {
    if (hasRated && myRating >= 1 && myRating <= 5) {
      setUserRatingValue(myRating);
    }
  }, [hasRated, myRating]);

  const isRatingDataLoading = useMemo(() => {
    return (
      isGymRatingLoading ||
      isGymRatingFetching ||
      (token && (isMyRatingLoading || isMyRatingFetching))
    );
  }, [
    isGymRatingLoading,
    isGymRatingFetching,
    token,
    isMyRatingLoading,
    isMyRatingFetching,
  ]);

  const handleOpenRatingModal = () => {
    if (!token) {
      showGlobalToast("برای ثبت امتیاز ابتدا وارد حساب کاربری شوید", "error");
      navigate("/auth");
      return;
    }

    setUserRatingValue(hasRated ? myRating : 0);
    setOpenRatingModal(true);
  };

  const handleCloseRatingModal = () => {
    if (isRatingSubmitting) {
      return;
    }

    setOpenRatingModal(false);

    if (!hasRated) {
      setUserRatingValue(0);
    }
  };

  const handleSubmitRating = async () => {
    if (!token) {
      showGlobalToast("برای ثبت امتیاز ابتدا وارد حساب کاربری شوید", "error");
      navigate("/auth");
      return;
    }

    if (hasRated) {
      showGlobalToast("شما قبلاً به این باشگاه امتیاز داده‌اید", "error");
      return;
    }

    if (!userRatingValue || userRatingValue < 1 || userRatingValue > 5) {
      showGlobalToast("لطفاً یک امتیاز بین ۱ تا ۵ انتخاب کنید", "error");
      return;
    }

    try {
      const response = await addRating({
        targetId: id,
        data: {
          targetType: "Gym",
          rating: userRatingValue,
        },
      }).unwrap();

      if (response?.success) {
        showGlobalToast(
          response?.message || "امتیاز شما با موفقیت ثبت شد",
          "success",
        );
        setOpenRatingModal(false);
      }
    } catch (error) {
      const message = error?.data?.message || "خطا در ثبت امتیاز";
      showGlobalToast(message, "error");
      console.error("Error submitting gym rating:", error);
    }
  };

  const getCoverImage = () => gymData?.gallery?.[0] || "/default-cover.jpg";
  const getLogo = () => gymData?.logo || null;
  const getRating = () => averageRating;
  const getTotalReviews = () => totalRatings;

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 5 }}>
        <LinearProgress color="secondary" />
      </Box>
    );
  }

  const pageTitle = gymData?.fullName
    ? `باشگاه ${gymData.fullName} | اطلاعات و ثبت‌نام`
    : "پروفایل باشگاه";

  const pageDesc =
    gymData?.about?.slice(0, 150) ||
    `اطلاعات کامل، امکانات، گالری تصاویر و ثبت‌نام در باشگاه ورزشی ${gymData?.name}.`;

  const seoImage = getLogo() ? import.meta.env.VITE_URL + getLogo() : undefined;

  return (
    <Box
      component="article"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        pb: 8,
        overflowX: "hidden",
        mt: { xs: "-60px", md: "-70px", lg: "-100px" },
        direction: "rtl",
      }}
    >
      <SEOHelmet
        title={pageTitle}
        description={pageDesc}
        keywords={`باشگاه ورزشی, ${gymData?.name || ""}, ثبت نام باشگاه, امکانات باشگاه`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={seoImage}
      />

      <Box
        component="section"
        sx={{
          position: "relative",
          height: { xs: 260, md: 360 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
          }}
        >
          <img
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            src={import.meta.env.VITE_URL + getCoverImage()}
            alt={gymData?.name || "notfound"}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(to bottom, rgba(18,18,18,0.2) 0%, ${theme.palette.background.default} 100%)`,
          }}
        />
      </Box>

      <Container
        maxWidth="xxl"
        sx={{
          px: { xs: 1.5, sm: 3, md: 5 },
          mt: { xs: -8, md: -10 },
          position: "relative",
          zIndex: 10,
        }}
      >
        <Box
          component="header"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-end" },
            gap: 3,
            mb: 5,
          }}
        >
          <Avatar
            src={getLogo() ? import.meta.env.VITE_URL + getLogo() : undefined}
            sx={{
              width: { xs: 110, md: 140 },
              height: { xs: 110, md: 140 },
              border: `4px solid ${theme.palette.background.default}`,
              boxShadow: `0 12px 32px ${theme.palette.secondary.main}40`,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              fontSize: "3rem",
              fontWeight: 800,
            }}
          >
            {!getLogo() && (gymData?.name?.charAt(0) || "G")}
          </Avatar>

          <Box sx={{ textAlign: { xs: "center", md: "right" }, pb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: "center", md: "flex-start" }}
              gap={1}
              mb={0.5}
            >
              <Typography
                component="h1"
                variant="h4"
                fontWeight={800}
                color="text.primary"
              >
                {gymData?.name || "باشگاه ورزشی"}
              </Typography>
              <Verified sx={{ color: "secondary.main", fontSize: 24 }} />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: "center", md: "flex-start" }}
              gap={1}
              mt={1}
            >
              <Box
                dir="ltr"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "rgba(0, 229, 255, 0.1)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <StarIcon sx={{ color: "secondary.main", fontSize: 18 }} />
                <Typography fontWeight={700} color="secondary.main">
                  {getRating().toLocaleString("fa-IR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                ({getTotalReviews().toLocaleString("fa-IR")} نظر)
              </Typography>

              {gymData?.genderType && (
                <Chip
                  label={
                    gymData.genderType === "women"
                      ? "ویژه بانوان"
                      : gymData.genderType === "men"
                        ? "ویژه آقایان"
                        : "آقایان و بانوان"
                  }
                  size="small"
                  sx={{
                    ml: 1,
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "text.primary",
                  }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" },
            gap: { xs: 2.5, lg: 4 },
            alignItems: "start",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ShortDetails
              gymData={gymData}
              onOpenRating={handleOpenRatingModal}
              averageRating={averageRating}
              totalRatings={totalRatings}
              myRating={myRating}
              hasRated={hasRated}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FullDetails gymData={gymData} gymId={id} />
          </motion.div>
        </Box>
      </Container>

      <Dialog
        open={openRatingModal}
        onClose={handleCloseRatingModal}
        PaperProps={{
          sx: {
            direction: "rtl",
            borderRadius: 4,
            bgcolor: "background.paper",
            backgroundImage: "none",
            minWidth: { xs: "90vw", sm: 400 },
            border: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 800,
            pt: 4,
            color: "text.primary",
          }}
        >
          {hasRated
            ? `امتیاز شما به باشگاه ${gymData?.name || ""}`
            : `امتیاز به باشگاه ${gymData?.name || ""}`}
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 3,
          }}
        >
          {isRatingDataLoading ? (
            <>
              <CircularProgress size={30} />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                در حال دریافت اطلاعات امتیاز...
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {hasRated
                  ? "شما قبلاً امتیاز خود را ثبت کرده‌اید و امکان تغییر آن وجود ندارد."
                  : "عملکرد و امکانات این باشگاه را چطور ارزیابی می‌کنید؟"}
              </Typography>

              <Box
                dir="ltr"
                sx={{ display: "flex", justifyContent: "center", mt: 1 }}
              >
                <Rating
                  value={hasRated ? myRating : userRatingValue}
                  onChange={(_, newValue) => {
                    if (!hasRated) {
                      setUserRatingValue(newValue || 0);
                    }
                  }}
                  readOnly={hasRated}
                  size="large"
                  icon={
                    <StarIcon
                      fontSize="inherit"
                      sx={{ color: theme.palette.secondary.main, mx: 0.5 }}
                    />
                  }
                  emptyIcon={
                    <StarIcon
                      fontSize="inherit"
                      sx={{ color: "rgba(255,255,255,0.1)", mx: 0.5 }}
                    />
                  }
                  sx={{ fontSize: "3rem" }}
                />
              </Box>

              {hasRated ? (
                <Typography
                  variant="body2"
                  color="primary.main"
                  textAlign="center"
                  fontWeight={700}
                >
                  امتیاز ثبت‌شده شما:{" "}
                  <Box
                    component="span"
                    dir="ltr"
                    sx={{ display: "inline-block" }}
                  >
                    {myRating} از ۵
                  </Box>
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  میانگین امتیاز این باشگاه:{" "}
                  <Box
                    component="span"
                    dir="ltr"
                    sx={{ display: "inline-block", fontWeight: 700 }}
                  >
                    {averageRating.toFixed(1)}
                  </Box>{" "}
                  از ۵
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 4 }}>
          <Button
            onClick={handleCloseRatingModal}
            disabled={isRatingSubmitting}
            sx={{ color: "text.secondary", fontWeight: 600 }}
          >
            بستن
          </Button>

          {!hasRated && !isRatingDataLoading && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmitRating}
              disabled={
                userRatingValue < 1 || userRatingValue > 5 || isRatingSubmitting
              }
              sx={{
                px: 4,
                borderRadius: 2,
                fontWeight: 800,
                color: "#000",
                boxShadow: `0 4px 15px ${theme.palette.secondary.main}50`,
              }}
            >
              {isRatingSubmitting ? "در حال ثبت..." : "ثبت امتیاز"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
